import React, { useMemo, useState } from "react";
import { TripX } from "@/types/trip";
import { ChevronDownIcon, Circle } from "lucide-react";
import { CalendarIcon, CheckIcon, MapPinIcon } from "@heroicons/react/20/solid";
import { motion } from "framer-motion";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { getFlagImage } from "@/lib/general";
import { format } from "date-fns";

interface UserActiveTripDropDownProps {
  trips: TripX[];
  selectedTrip: TripX | null;
  onTripSelect: (trip: TripX) => void;
}

const UserActiveTripDropDown: React.FC<UserActiveTripDropDownProps> = ({
  trips,
  selectedTrip,
  onTripSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const TripItem = ({ trip }: { trip: TripX }) => {
    const isSelected = selectedTrip?.id === trip.id;

    const locationNames: { name: string; country: string }[] = useMemo(() => {
      return trip.locations.map((location) => ({
        name: location.name,
        country: location.address_components.country,
      }));
    }, [trip.locations]);

    // Get the first and last location for display
    const firstLocation = locationNames[0];
    const lastLocation =
      locationNames.length > 1 ? locationNames[locationNames.length - 1] : null;

    // Format trip dates if available
    const tripDates = useMemo(() => {
      if (trip.locations && trip.locations.length > 0) {
        const firstLoc = trip.locations[0];
        const lastLoc = trip.locations[trip.locations.length - 1];

        if (firstLoc?.date?.start_date && lastLoc?.date?.end_date) {
          return {
            start: format(new Date(firstLoc.date.start_date), "MMM d"),
            end: format(new Date(lastLoc.date.end_date), "MMM d, yyyy"),
          };
        }
      }
      return null;
    }, [trip.locations]);

    return (
      <motion.div
        role="option"
        aria-selected={isSelected}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        whileHover={{
          backgroundColor: isSelected
            ? "rgba(79, 70, 229, 0.08)"
            : "rgba(243, 244, 246, 0.8)",
        }}
        transition={{ duration: 0.2 }}
        className={`${
          isSelected ? "bg-indigo-50" : "hover:bg-neutral-50"
        } px-4 py-3 cursor-pointer rounded-lg transition-colors group`}
        onClick={() => {
          onTripSelect(trip);
          setIsOpen(false);
        }}
      >
        <div className="flex flex-col space-y-2">
          {/* Trip name and selection indicator */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* circle filled or not filled */}
              <div
                className={`w-2 h-2 rounded-full transition-all ${
                  isSelected
                    ? "bg-indigo-600 ring-1 ring-indigo-600 ring-offset-1"
                    : "bg-neutral-200 ring-1 ring-neutral-200 ring-offset-1 group-hover:bg-neutral-400 group-hover:ring-neutral-400"
                }`}
              />
              <span className="font-normal text-neutral-800 text-xs">
                {trip.name}
              </span>
            </div>
            {isSelected && <CheckIcon className="w-4 h-4 text-indigo-600" />}
          </div>
        </div>
      </motion.div>
    );
  };

  const triggerButton = (
    <button
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-label="Select a trip"
      className="flex items-center space-x-2 px-3 py-2 text-sm font-normal rounded-full
               transition ease-in-out hover:shadow hover:shadow-indigo-200 duration-300 bg-white text-indigo-800 ring-1 ring-indigo-800"
    >
      <span className="max-w-[150px] truncate">
        {selectedTrip?.name || "Select a trip"}
      </span>
      <motion.div
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.3 }}
      >
        <ChevronDownIcon className="w-4 h-4" />
      </motion.div>
    </button>
  );

  return (
    <CustomDropdown
      trigger={triggerButton}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      position="right"
      title="Select a trip"
      size="md"
      className="p-2"
    >
      <div className="max-h-[300px] overflow-y-auto space-y-2 custom-scrollbar w-80">
        {trips.length > 0 ? (
          trips.map((trip) => <TripItem key={trip.id} trip={trip} />)
        ) : (
          <div className="px-4 py-8 text-sm text-gray-500 text-center">
            <div className="flex flex-col items-center gap-2">
              <CalendarIcon className="w-8 h-8 text-gray-300" />
              <p>No trips available</p>
            </div>
          </div>
        )}
      </div>
    </CustomDropdown>
  );
};

export default UserActiveTripDropDown;
